import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { connect } from 'react-redux';
import PageTitle from '../../components/PageTitle';
import Header from '../../components/Header';
import './styles.css';
import SubscriptionModule from '../../components/SubscriptionModule';
import SubscriptionUpgradeForm from '../SubscriptionUpgradeForm';
import RepVisitsUpgradeForm from '../RepVisitsUpgradeForm';
import UpgradeForm from '../UpgradeForm';
import SubscriptionActive from '../../components/SubscriptionActive';
import SubscriptionInactive from '../../components/SubscriptionInactive';
import HasRole from '../../components/HasRole';
import conferenceSvg from './conference.svg';
import calendarSvg from './calendar-4.svg';
import universitySvg from './university.svg';
import settingsSvg from './settings-window.svg';
import * as constants from '../Authorization/constants';
import * as marketoUtils from '../../utils/marketoForm';
import FeatureEnabled from '../../components/FeatureEnabled';
import FeatureDisabled from '../../components/FeatureDisabled';
import CollegeEventsSubscriptionModule from '../../components/CollegeEventsFrame/college-events-subscription-module';

import { connectionProps, nextGenConnectionProps, advAwareProps, scholarshipsProps } from './constants';
import { withFreemiumPropQuery, withEnabledFeatures } from '../../utils/features';
import { withCommunityProfileIdQuery } from '../../utils/community';

const subscriptions = [
  'amplus',
  'events',
  'advancedawareness',
  'connection',
  'scholarships',
];

const repVisitsSubscription = 'repvisits';
const communitySubscription = 'community';
const collegeProfileSubscription = 'collegeprofile';

function isSubscription(subscription, subscriptionType) {
  const name = (subscription || '').toLowerCase();
  return name === subscriptionType;
}

export function normalizeSubscription(subscription) {
  if (!subscription) return null;

  const name = subscription.toLowerCase();
  return subscriptions.includes(name) ? name : null;
}

class SubscriptionsPage extends React.Component {
  static propTypes = {
    router: PropTypes.shape({
      params: PropTypes.object.isRequired,
      push: PropTypes.func.isRequired,
    }).isRequired,
    current: PropTypes.shape({
      viewer: PropTypes.shape({
        idToken: PropTypes.shape({
          given_name: PropTypes.string.isRequired,
          name: PropTypes.string.isRequired,
        }).isRequired,
      }).isRequired,
    }).isRequired,
    enabledFeatures: PropTypes.array,
    activeSubscriptions: PropTypes.array,
    freemium: PropTypes.bool,
  };

  componentDidMount() {
    const { enabledFeatures, activeSubscriptions, freemium } = this.props;
    const { COMMUNITY_SERVERLESS, MATCHING_SUBSCRIPTION } = constants;
    const isFreemium = freemium || !activeSubscriptions?.includes(MATCHING_SUBSCRIPTION);

    if (enabledFeatures?.includes(COMMUNITY_SERVERLESS) && isFreemium) {
      window.addEventListener('counselor-community-serverless-subscribe-community', this.routeChange);
    }
  }

  routeChange = () => {
    this.props.router.push('/subscribe/community');
  };

  onCancelUpgrade = () => {
    this.props.router.push('/');
  };

  onTriggerUpgrade = (subscription) => {
    if (!subscription) {
      throw new Error('Required parameter "subscription" is missing.');
    }
    this.props.router.push(`/subscribe/${subscription}`);
  };

  getUserFirstNameFromViewer() {
    const { current } = this.props;
    /* istanbul ignore next */
    return get(current, 'viewer.idToken.given_name') || get(current, 'viewer.idToken.name');
  }

  render() {
    /* istanbul ignore next */
    const { router: { params: { subscription } } = { params: {} } } = this.props;
    // normalize subscription url param, ignoring unknown/invalid values
    const normalizedSubscription = normalizeSubscription(subscription);
    const isRepVisitsUpgradeSubscription = isSubscription(subscription, repVisitsSubscription);
    const isCommunityUpgradeSubscription = isSubscription(subscription, communitySubscription);
    const isCollegeProfileUpgradeSubscription = isSubscription(subscription, collegeProfileSubscription);

    const isServerlessCommunityOn = get(this.props, 'enabledFeatures', []).includes(constants.COMMUNITY_SERVERLESS);
    const communityProfileId = get(this.props, 'communityProfile.viewer.profile.communityId', '');
    const communityIdMatchesUUID = /\b[0-9a-f]{8}\b-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-\b[0-9a-f]{12}\b/.test(communityProfileId);
    const isCommunityProfileServerless = !communityProfileId || communityIdMatchesUUID;
    const canAccessLegacyCommunity = !isServerlessCommunityOn || !isCommunityProfileServerless;

    return (
      <main aria-labelledby="pageTitle" styleName="main">
        <PageTitle title="Home" />
        <div styleName="content" id="content">
          <Header as="h1" size="huge" styleName="page-header" id="pageTitle">Welcome, {this.getUserFirstNameFromViewer()}</Header>
          <div styleName="modules-wrapper">
            <HasRole anyOf={[constants.ADMIN_ROLE, constants.PUBLISHING_ROLE]}>
              <FeatureEnabled allOf={constants.CAMINO_HEM_TRANSFER_INFO}>
                <SubscriptionModule
                  color="blue"
                  category="awareness"
                  title="College Profile"
                  description="Manage your institutional profile so that students and staff have the most up-to-date information."
                  buttonLabel="Update"
                  image={universitySvg}
                  link="/naviance-college-profile/edit"
                />
              </FeatureEnabled>
              <FeatureDisabled anyOf={constants.CAMINO_HEM_TRANSFER_INFO}>
                <SubscriptionModule
                  color="blue"
                  category="awareness"
                  title="Naviance College Profile"
                  description={'Manage your institutional profile so that high ' +
                  'school students and staff have the most up-to-date information.'}
                  buttonLabel="Update"
                  image={universitySvg}
                  link="/naviance-college-profile/edit"
                />
              </FeatureDisabled>
            </HasRole>
            {(process.env.COMMUNITY_URL || process.env.COUNSELOR_COMMUNITY_URL) && (
              <React.Fragment>
                <FeatureEnabled allOf={constants.COMMUNITY_SERVERLESS}>
                  <SubscriptionModule
                    color="blue"
                    category="awareness"
                    title="Counselor Community"
                    description="Find high school staff, access high school profiles, and export contact information."
                    buttonLabel="Explore"
                    image={conferenceSvg}
                    link="/counselorcommunity/"
                  />
                </FeatureEnabled>
                {!!canAccessLegacyCommunity && (
                  <FeatureDisabled anyOf={constants.DISABLE_LEGACY_COMMUNITY}>
                    <SubscriptionModule
                      color="blue"
                      category="awareness"
                      title="Counselor Community"
                      description={'Engage with high school staff, access high school ' +
                      'profiles, and collaborate with the broader admissions community.'}
                      buttonLabel="Participate"
                      image={conferenceSvg}
                      link="/counselor-community/"
                    />
                  </FeatureDisabled>
                )}
              </React.Fragment>
            )}
            <HasRole anyOf={[constants.ADMIN_ROLE, constants.PUBLISHING_ROLE]}>
              <SubscriptionActive anyOf={constants.ADV_AWARENESS_SUBSCRIPTION}>
                <SubscriptionModule {...advAwareProps} />
              </SubscriptionActive>
              <SubscriptionInactive allOf={constants.ADV_AWARENESS_SUBSCRIPTION}>
                <SubscriptionModule
                  {...advAwareProps}
                  showUpgrade
                  name="advancedawareness"
                  onTriggerUpgrade={this.onTriggerUpgrade}
                />
              </SubscriptionInactive>
            </HasRole>
            <FeatureEnabled allOf={constants.SCHOLARSHIPS_SUBSCRIPTION}>
              <HasRole anyOf={[constants.ADMIN_ROLE, constants.PUBLISHING_ROLE]}>
                <SubscriptionActive anyOf={constants.SCHOLARSHIPS_SUBSCRIPTION}>
                  <SubscriptionModule {...scholarshipsProps} />
                </SubscriptionActive>
                <SubscriptionInactive allOf={constants.SCHOLARSHIPS_SUBSCRIPTION}>
                  <SubscriptionModule
                    {...scholarshipsProps}
                    showUpgrade
                    name="scholarships"
                    onTriggerUpgrade={this.onTriggerUpgrade}
                  />
                </SubscriptionInactive>
              </HasRole>
            </FeatureEnabled>
            <HasRole anyOf={constants.ADMIN_ROLE}>
              <SubscriptionActive anyOf={constants.AM_PLUS_SUBSCRIPTION}>
                <SubscriptionActive anyOf={constants.CONNECTION_SUBSCRIPTION}>
                  <SubscriptionModule {...nextGenConnectionProps} />
                </SubscriptionActive>
                <SubscriptionInactive allOf={constants.CONNECTION_SUBSCRIPTION}>
                  <SubscriptionModule {...connectionProps} />
                </SubscriptionInactive>
              </SubscriptionActive>
              <SubscriptionInactive allOf={constants.AM_PLUS_SUBSCRIPTION}>
                <SubscriptionActive anyOf={[constants.CONNECTION_SUBSCRIPTION, constants.CAMINO_SUBSCRIPTION]}>
                  <SubscriptionModule {...nextGenConnectionProps} />
                </SubscriptionActive>
                <SubscriptionInactive allOf={[constants.CONNECTION_SUBSCRIPTION, constants.CAMINO_SUBSCRIPTION]}>
                  <SubscriptionModule
                    {...nextGenConnectionProps}
                    showUpgrade
                    name="connection"
                    onTriggerUpgrade={this.onTriggerUpgrade}
                  />
                </SubscriptionInactive>
              </SubscriptionInactive>
            </HasRole>
            <SubscriptionModule
              color="darkcyan"
              category="presence"
              title="RepVisits"
              description="Create a travel plan and schedule visits with ease."
              buttonLabel="Schedule"
              image={calendarSvg}
              link="/rep-visits/"
            />
            <HasRole anyOf={[constants.ADMIN_ROLE, constants.PUBLISHING_ROLE]}>
              <CollegeEventsSubscriptionModule onTriggerUpgrade={this.onTriggerUpgrade} />
            </HasRole>
            <SubscriptionModule
              color="grey"
              category="settings"
              title="Account Settings"
              description="Update your account settings."
              buttonLabel="Configure"
              image={settingsSvg}
              link="/settings/"
            />
          </div>
        </div>
        {normalizedSubscription && (
        <SubscriptionUpgradeForm
          open
          subscription={normalizedSubscription}
          onClose={this.onCancelUpgrade}
          marketoUtils={marketoUtils}
        />
        )}
        {isRepVisitsUpgradeSubscription && (
          <RepVisitsUpgradeForm open={isRepVisitsUpgradeSubscription} onClose={this.onCancelUpgrade} />
        )}
        {isCommunityUpgradeSubscription && (
          <UpgradeForm open={isCommunityUpgradeSubscription} onClose={this.onCancelUpgrade} />
        )}
        {isCollegeProfileUpgradeSubscription && (
          <SubscriptionUpgradeForm open={isCollegeProfileUpgradeSubscription} onClose={this.onCancelUpgrade} marketoUtils={marketoUtils} />
        )}
      </main>
    );
  }
}

/* istanbul ignore next */
const mapStateToProps = (state) => ({
  current: state.current,
});

export const ConnectedSubscriptionsPage = connect(mapStateToProps)(SubscriptionsPage);
export default withCommunityProfileIdQuery(withFreemiumPropQuery(withEnabledFeatures(ConnectedSubscriptionsPage)));
